import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	connectToRouter,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import { SettingsPage } from "../SettingsPage";
import {
	Form,
	FormItem,
	Tabs,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation,
	useWarnBeforeUnload,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty
} from "client/Validation/rules";
import {
	ActionKind,
	ActionsWithContext,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	getApp,
	IntegrationAppSaveModel,
	IntegrationAppViewModel,
	updateApp
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import {
	EMPTY_ARRAY,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import {
	FlowActionContext,
	FlowsTabContent
} from "./FlowsTabContent";
import {
	SharedSettingActionContext,
	SharedSettingsTab
} from "./SharedSettingsTab";
import { integrationBuilderRouting } from "./OverViewPage";
import { useUnsavedChangesDialog } from "./UnsavedChangesDialog";
import styles from "./EditIntegrationAppPage.module.less";

export type EditIntegrationAppPageProps = {
	onNavigate: NavigationCallback;
	appId: number;
	tab?: string;
};

export const EditIntegrationAppPage = (props: EditIntegrationAppPageProps): ReactElement => {
	const {
		appId,
		tab,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [app, setApp] = useState<IntegrationAppViewModel | null>(null);
	const [initialFormData, setInitialFormData] = useState<IntegrationAppSaveModel>(defaultFormData);
	const [formData, setFormData] = useState<IntegrationAppSaveModel>(defaultFormData);
	const [topBarActionState, setTopBarActionState] = useState<IntegrationAppActionsWithContext>(emptyActionState);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, loadVersion] = useForceRerender();
	const [unsavedChangesDialog, openUnsavedChangesDialog] = useUnsavedChangesDialog();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const formHandler: IFormHandler<IntegrationAppSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useActiveSettingsMenuItem(SettingsPageKind.IntegrationBuilder);

	const activeTabId: TabId = resolveTabId(tab);
	const isDataChanged: boolean = formData.name !== initialFormData.name;

	useWarnBeforeUnload(isDataChanged);

	const activeTabIdChangeHandler = useCallback(
		(tabId: TabId): void => {
			onNavigate(
				integrationBuilderRouting.editApp({
					routeParams: {
						appId: `${appId}`
					},
					queryParams: {
						tab: tabId
					}
				}),
				{
					shouldReplace: true
				}
			);
		},
		[appId, onNavigate]
	);

	const backToOverviewHandler = useCallback(
		(): void => {
			onNavigate({
				path: integrationBuilderRouting.overview
			});
		},
		[onNavigate]
	);

	const saveHandler = useCallback(
		async (): Promise<boolean> => {
			const validationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				activeTabIdChangeHandler("general");
				return false;
			}

			setIsBusy(true);

			try {
				await updateApp(
					appId,
					formData,
					globalCancellation
				);

				setInitialFormData(formData);

				setApp(
					previousApp => isNotNil(previousApp)
						? {
							...previousApp,
							name: formData.name
						}
						: previousApp
				);

				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullySavedTheApp"));

				return true;
			} catch (e) {
				if (isCancelledError(e)) {
					return false;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);

				return false;
			}
		},
		[
			activeTabIdChangeHandler,
			alertManager,
			appId,
			formData,
			formHandler.validation,
			globalCancellation
		]
	);

	const confirmUnsavedChanges = useCallback(
		async (): Promise<boolean> => {
			if (!isDataChanged) {
				return true;
			}

			const result: TryResult<boolean> = await openUnsavedChangesDialog({});

			if (!result.wasSuccessful) {
				return false;
			}

			return result.result
				? saveHandler()
				: true;
		},
		[
			isDataChanged,
			openUnsavedChangesDialog,
			saveHandler
		]
	);

	const backHandler = useCallback(
		async (): Promise<void> => {
			const isConfirmed: boolean = await confirmUnsavedChanges();

			if (!isConfirmed) {
				return;
			}

			backToOverviewHandler();
		},
		[
			backToOverviewHandler,
			confirmUnsavedChanges
		]
	);

	const topBarActionsStateChangeHandler = useCallback(
		(
			actions: readonly ToolbarAction<ActionContext>[],
			context: ActionContext
		): void => {
			setTopBarActionState({ actions, context });
		},
		[]
	);

	const generalActions = useMemo(
		(): readonly ToolbarAction<ActionContext>[] => buildActionsForTopBar<
			ActionContext,
			ToolbarAction<ActionContext>
		>(
			{
				defaultActions: listGeneralActions({
					save: saveHandler,
					cancel: backToOverviewHandler
				})
			},
			false,
			isResponsive,
			false
		),
		[
			backToOverviewHandler,
			isResponsive,
			saveHandler
		]
	);

	const {
		actions,
		context
	} = activeTabId === "general"
		? {
			actions: generalActions,
			context: emptyActionContext
		}
		: topBarActionState;

	const hasSelection: boolean = context.selection
		.length !== 0;

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			async function fetchApp(id: number, signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const result: IntegrationAppViewModel = await getApp(id, signal)
						.catch(handlePageDataLoadingError);

					const savedFormData: IntegrationAppSaveModel = { name: result.name };

					setApp(result);
					setInitialFormData(savedFormData);
					setFormData(savedFormData);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();
			fetchApp(appId, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			appId,
			loadVersion
		]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				title={resolveFormattedTextString({
					template: localize("_EditNameApp"),
					replacements: [
						{
							what: "{{Name}}",
							filler: app?.name ?? ""
						}
					]
				})}
				actions={actions}
				actionContext={context}
				hasSelection={hasSelection}
				onBack={backHandler}
				backButtonText={localize("_Apps")}
			>
				<SettingsPage.Section height="stretch">
					<Tabs<TabId>
						height="fill"
						activeTabId={activeTabId}
						onActiveTabIdChange={activeTabIdChangeHandler}
					>
						<Tabs.Tab<TabId>
							id="general"
							title={localize("_General")}
						>
							<Form onSubmit={saveHandler}>
								<FormItem label={localize("_Name")} isRequired>
									<TextInput
										{...formHandler.for("name")
											.attachToTextInput()
										}
										placeholder={localize("_EnterAppName")}
									/>
									<ValidationError
										{...formHandler.for("name").attachToValidationError()}
									/>
								</FormItem>
							</Form>
						</Tabs.Tab>
						{isNotNil(app) && (
							<>
								<Tabs.Tab<TabId>
									id="flows"
									title={localize("_Flows")}
								>
									<div className={styles.tabContent}>
										<FlowsTabContent
											appId={appId}
											flows={app.flows}
											creatableKinds={app.creatableKinds}
											isActive={activeTabId === "flows"}
											onActionsStateChange={topBarActionsStateChangeHandler}
											onConfirmUnsavedChanges={confirmUnsavedChanges}
											onNavigate={onNavigate}
											onReload={reload}
										/>
									</div>
								</Tabs.Tab>
								<Tabs.Tab<TabId>
									id="shared-settings"
									title={localize("_SharedSettings")}
								>
									<div className={styles.tabContent}>
										<SharedSettingsTab
											appId={appId}
											sharedSettings={app.sharedSettings}
											isActive={activeTabId === "shared-settings"}
											onActionsStateChange={topBarActionsStateChangeHandler}
											onConfirmUnsavedChanges={confirmUnsavedChanges}
											onReload={reload}
										/>
									</div>
								</Tabs.Tab>
							</>
						)}
					</Tabs>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{unsavedChangesDialog}
		</>
	);
};
EditIntegrationAppPage.displayName = "EditIntegrationAppPage";

export type IntegrationAppTabId =
	| "general"
	| "flows"
	| "shared-settings";

export type EditIntegrationAppRouteParams = RouteParametersFromProps<
	EditIntegrationAppPageProps,
	"appId"
>;

export type EditIntegrationAppQueryParams = QueryParametersFromProps<
	EditIntegrationAppPageProps,
	"tab"
> & {
	tab?: IntegrationAppTabId;
};

export const EditIntegrationAppPageForRouter = connectToRouter(
	EditIntegrationAppPage,
	{
		"appId": {
			isRequired: true,
			tryParse: tryParseInt,
			type: "route"
		},
		"tab": {
			isRequired: false,
			type: "query"
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type TabId = IntegrationAppTabId;

function resolveTabId(tab: string | undefined): TabId {
	return tab === "flows" || tab === "shared-settings"
		? tab
		: "general";
}

type ActionContext =
	| FlowActionContext
	| SharedSettingActionContext;

type IntegrationAppActionsWithContext = ActionsWithContext<
	ActionContext,
	ToolbarAction<ActionContext>
>;

const emptyActionContext: ActionContext = {
	selection: EMPTY_ARRAY,
	isMobile: false
};

const emptyActionState: IntegrationAppActionsWithContext = {
	actions: EMPTY_ARRAY,
	context: emptyActionContext
};

const defaultFormData: IntegrationAppSaveModel = {
	name: ""
};

const validationConfiguration: IValidationConfiguration<IntegrationAppSaveModel> =
	createValidationConfiguration<IntegrationAppSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

type GeneralActionHandlers = {
	save: () => void;
	cancel: () => void;
};

function listGeneralActions(
	handlers: GeneralActionHandlers
): readonly ToolbarAction<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true,
			icon: "floppy-disk"
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.cancel(),
			icon: "xmark"
		}
	];
}
